import {
  DeleteOutlined,
  LeftOutlined,
  PauseOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Tooltip } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import {
  MyLeadsFilterTypes,
  MyLeadsFriendlyName,
  MyleadsRelationalOperators,
  MyleadsRelationalOperatorType,
  MyLeadsSearchQuery,
} from '@mm/state/interfaces/myLeads';
import { InputNumber, InputNumberProps, TimePicker } from 'antd';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { setPlaceholderByKey } from '../helpers';
import { useFilterParams } from '../use-filter-params';
import styles from './EventsFilter.module.css';

type Props = {
  attributeKey: string;
  withEqual?: boolean;
  inputType?: 'time' | 'number';
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
  onDelete: (key: string) => void;
  inputProps?: InputNumberProps<number>;
};

export const EventFilterRow = ({
  attributeKey,
  withEqual,
  inputType = 'number',
  filterParams,
  setFilterParams,
  onDelete,
  inputProps,
}: Props) => {
  const currentValue: [{ [key: string]: string }] =
    filterParams.query?.attributes?.[attributeKey];
  const defaults = Object.entries(currentValue?.[0] || {}).flat();
  const defaultOperator = defaults?.[0] as MyleadsRelationalOperatorType;
  const defaultInputValue = defaults?.[1];
  const greaterThan = MyleadsRelationalOperators.GREATER_THAN;
  const [operator, setOperator] = useState<MyleadsRelationalOperatorType>(
    defaultOperator || greaterThan,
  );
  const [inputValue, setInputValue] = useState<string | null>(
    defaultInputValue || null,
  );

  const debouncedInputValue = useDebounce(inputValue?.toString(), 500);

  const { primaryColor } = useWhitelabelConfig();
  const activeWhitelabelStyles = {
    color: primaryColor,
    borderColor: primaryColor,
  };

  const formatterFn = (minutes: number | null, secs: number | null = 0) => {
    const seconds = (minutes || 0) * 60 + (secs || 0);
    return seconds;
  };

  const { getUpdatedFilterParams, shouldDisableFilters } = useFilterParams({
    filterParams,
  });

  const onTimeInputChange = (value: Dayjs) => {
    const time = formatterFn(
      value ? value.minute() : 0,
      value ? value.second() : 0,
    ).toString();
    setInputValue(time);
  };

  const onInputChange = (value: string | null) => {
    setInputValue(value);
  };

  const secondsToDayjs = useMemo(() => {
    if (inputValue) {
      const startOfDay = dayjs().startOf('day');
      return startOfDay.add(Number(inputValue), 'second');
    }
    return undefined;
  }, [inputValue]);

  const updateFilterParams = (isExisting: boolean) => {
    if (currentValue) {
      delete filterParams.query?.attributes?.[attributeKey];
    }

    const updatedQuery = getUpdatedFilterParams({
      key: attributeKey,
      value:
        (operator === 'eq'
          ? debouncedInputValue
          : { [operator]: debouncedInputValue }) || '',
      isExisting,
      type: MyLeadsFilterTypes.attributes,
    });
    const newValue = updatedQuery?.query?.attributes?.[attributeKey];

    if (currentValue !== newValue) {
      setFilterParams(updatedQuery);
    }
  };

  useEffect(() => {
    if (
      debouncedInputValue !== defaultInputValue ||
      operator !== defaultOperator
    ) {
      updateFilterParams(!debouncedInputValue);
    }
  }, [debouncedInputValue, operator]);

  useEffect(() => {
    if (!currentValue) {
      setInputValue(null);
    }
  }, [currentValue]);

  useEffect(() => {
    if (defaultOperator) {
      setOperator(defaultOperator);
    }
  }, [filterParams.query?.attributes]);

  return (
    <div className={styles.eventRow}>
      <span className={styles.eventName}>
        {MyLeadsFriendlyName[attributeKey]}
      </span>
      <div className={styles.operators}>
        <Tooltip title="Less than">
          <span
            className={classNames(styles.operator, styles.leftArrow, {
              [styles.activeOperator]:
                operator === MyleadsRelationalOperators.LESS_THAN,
            })}
            onClick={() => setOperator(MyleadsRelationalOperators.LESS_THAN)}
            style={
              operator === MyleadsRelationalOperators.LESS_THAN
                ? activeWhitelabelStyles
                : {}
            }
          >
            <LeftOutlined />
          </span>
        </Tooltip>
        {withEqual && (
          <Tooltip title="Equal">
            <span
              className={classNames(styles.operator, styles.equal, {
                [styles.activeOperator]:
                  operator !== MyleadsRelationalOperators.LESS_THAN &&
                  operator !== MyleadsRelationalOperators.GREATER_THAN,
              })}
              onClick={() => setOperator('eq')}
              style={
                operator !== MyleadsRelationalOperators.LESS_THAN &&
                operator !== MyleadsRelationalOperators.GREATER_THAN
                  ? activeWhitelabelStyles
                  : {}
              }
            >
              <PauseOutlined className={styles.equalIcon} />
            </span>
          </Tooltip>
        )}

        <Tooltip title="More than">
          <span
            className={classNames(styles.operator, styles.rightArrow, {
              [styles.activeOperator]:
                operator === MyleadsRelationalOperators.GREATER_THAN,
            })}
            onClick={() => setOperator(greaterThan)}
            style={
              operator === MyleadsRelationalOperators.GREATER_THAN
                ? activeWhitelabelStyles
                : {}
            }
          >
            <RightOutlined />
          </span>
        </Tooltip>
      </div>
      <div className={styles.eventInput}>
        {inputType === 'time' ? (
          <TimePicker
            value={secondsToDayjs}
            onChange={onTimeInputChange}
            format="mm:ss"
            showNow={false}
            placeholder={setPlaceholderByKey(attributeKey)}
            getPopupContainer={triggerNode =>
              triggerNode.parentElement || document.body
            }
          />
        ) : (
          <InputNumber
            className={styles.numberInput}
            min={1}
            maxLength={6}
            value={inputValue}
            onChange={onInputChange}
            disabled={shouldDisableFilters}
            placeholder={setPlaceholderByKey(attributeKey)}
            {...(inputProps || {})}
          />
        )}

        <DeleteOutlined
          onClick={() => {
            onDelete(attributeKey);
            updateFilterParams(true);
          }}
        />
      </div>
    </div>
  );
};
